import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus } from 'lucide-react';
import SampleReportSection from '../components/SampleReportSection';
import analysisImage from '../assets/images/analysis.jpg';

const ResearchService = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    {
      question: "What type of businesses does Climb Capital focus on for research?",
      answer: "We focus on companies with sustainable competitive advantages, ethical leadership, and significant growth potential within a 3–5-year timeframe. Our research is not confined by market capitalization or sector."
    },
    {
      question: "How does Climb Capital ensure capital protection in its research strategy?",
      answer: "We strategically avoid high-risk sectors, including commodities and companies with substantial debt. Our disciplined process includes both qualitative and quantitative assessments to navigate market fluctuations."
    },
    {
      question: "Does Climb Capital provide frequent stock recommendations?",
      answer: "We prioritize quality over quantity, recognizing that a few well-chosen investments can yield substantial returns. Our goal is to create enduring wealth rather than chase transient trading opportunities."
    }
  ];

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <h1 className="text-4xl font-bold mb-8 text-gray-900 tracking-wide">
              RESEARCH SERVICE
            </h1>

            <div className="space-y-6 text-gray-700 leading-relaxed">
              <div>
                <h3 className="text-lg font-bold mb-3 text-[#1b2d4a]">Research Services : An Opportunistic Approach</h3>
                <p className="mb-4">
                  At Climb Capital, we provide stock ideas as a focused service, delivering curated investment 
                  opportunities to empower your decision-making. Our goal is to equip you with valuable insights 
                  while you maintain full control over your investment choices. Our stock research is not confined 
                  by market capitalization or sector; instead, we conduct comprehensive analyses particularly emerging 
                  companies to uncover opportunities that offer high absolute returns with limited downside risk.
                </p>
              </div>

              <p>
                We focus on companies with sustainable competitive advantages, ethical leadership, and significant 
                growth potential within a 3–5-year timeframe.
              </p>

              <p>
                We prioritize quality over quantity, recognizing that a few well-chosen investments can yield 
                substantial returns. Our disciplined process, which includes both qualitative and quantitative 
                assessments, allows us to navigate market fluctuations and position our clients advantageously.
              </p>

              <p>
                Our goal is to create enduring wealth rather than chase transient trading opportunities.
              </p>

              <div className="mt-8">
                <h3 className="text-lg font-bold mb-2 text-[#1b2d4a]">Risk Avoidance</h3>
                <p>
                  We strategically avoid high-risk sectors, including commodities and companies with substantial debt. 
                  Our primary objective is to generate wealth while safeguarding our clients' capital.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Research Strategy Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            {/* Left - Image */}
            <div>
              <img 
                src={analysisImage} 
                alt="Research Strategy" 
                className="w-full rounded-lg"
              />
            </div>
            
            {/* Right - Content */}
            <div>
              <h2 className="text-4xl font-bold mb-6 text-gray-900">
                Research Strategy
              </h2>
              <p className="text-gray-600 mb-8 leading-relaxed">
                Climb Capital's research strategy emphasizes the identification of high-value, 
                long-term growth opportunities over short-term trading tactics. We focus on 
                companies with sustainable competitive advantages, ethical leadership, and 
                significant growth potential within a 3–5-year timeframe. Through rigorous 
                qualitative and quantitative analysis, we strategically avoid high-risk sectors, 
                including commodities and companies with substantial debt. Our primary 
                objective is to generate wealth while safeguarding our clients' capital.
              </p>
              
              {/* FAQ Accordion */}
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div key={index} className="border-b border-gray-200 pb-4">
                    <button
                      onClick={() => toggleFaq(index)}
                      className="flex items-start gap-3 w-full text-left"
                    >
                      <Plus 
                        size={20} 
                        className={`text-yellow-500 mt-1 flex-shrink-0 transition-transform ${openFaq === index ? 'rotate-45' : ''}`} 
                      />
                      <span className="font-bold text-gray-900">{faq.question}</span>
                    </button>
                    {openFaq === index && (
                      <div className="mt-3 ml-8 text-gray-600">
                        {faq.answer}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sample Report Section */}
      <SampleReportSection />

      {/* CTA Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="bg-[#1b2d4a] rounded-2xl p-12 text-center text-white max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-4">
              Ready to Start Your Investment Journey?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Join thousands of investors who trust Climb Capital for their investment decisions
            </p>
            <Link
              to="/pricing"
              className="inline-block bg-yellow-400 text-gray-900 px-8 py-4 rounded-full font-bold hover:bg-yellow-500 transition-colors"
            >
              View Our Plans
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ResearchService;
